import { CancellationReason } from '@/api/generated-catch-backend/model';
import { Button, Flex, Modal, RadioGroup, Text, Textarea } from '@cmpsr/components';
import { Controller, useForm } from 'react-hook-form';
import { QuestionOption } from '@/components/decision-tree/components/SurveyItem/components/Question/components/QuestionOption';

type ModalCancelMembershipProps = {
  isOpen: boolean;
  onCloseClick: () => void;
  onSubmit: (reason: CancellationReason, reasonDescription?: string) => void;
  isTrialUser?: boolean;
  isSubmitting?: boolean;
};

type FormData = {
  reason: CancellationReason;
  reasonDescription?: string;
};

const CANCELLATION_REASONS = [
  { value: CancellationReason.TOO_EXPENSIVE, label: 'Too expensive' },
  { value: CancellationReason.NOT_USEFUL, label: "I didn't find it useful" },
  { value: CancellationReason.JUST_TESTING, label: 'I only wanted to try it' },
  { value: CancellationReason.TECHNICAL_ISSUES, label: 'Technical issues' },
  { value: CancellationReason.CONCERNED_ABOUT_RESULTS, label: 'Concerned about my results' },
  { value: CancellationReason.OTHER, label: 'Other' },
];

export default function ModalCancelMembership({
  isOpen,
  onCloseClick,
  onSubmit,
  isTrialUser = false,
  isSubmitting = false,
}: ModalCancelMembershipProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  const selectedReason = watch('reason');
  const isOtherSelected = selectedReason === CancellationReason.OTHER;

  const onFormSubmit = (data: FormData) => {
    onSubmit(data.reason, data.reasonDescription);
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseClick} isCentered>
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        bg="background-page"
        borderRadius="radii-2"
        border="1px solid"
        borderColor="ui-element-outline-default"
        width={{ base: '95%', md: '603px' }}
        p={{ base: 'spacer-5', lg: 'spacer-10' }}
        gap="spacer-6"
      >
        <Modal.Header bg="background-page" p={0} textAlign="center">
          <Text variant="text-header-S">
            We're sorry to see you go. Before you cancel, can you tell us why? This helps us improve and could unlock
            options that fit you better.
          </Text>
        </Modal.Header>
        <Modal.Body bg="background-page" p={0}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Flex flexDirection="column" gap="spacer-6">
              <Controller
                name="reason"
                control={control}
                rules={{ required: 'Please select a reason' }}
                render={({ field }) => (
                  <RadioGroup onChange={field.onChange} value={field.value}>
                    {CANCELLATION_REASONS.map(({ value, label }) => (
                      <QuestionOption
                        key={value}
                        componentType="radio"
                        componentProps={{
                          value,
                        }}
                        label={label}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              {errors.reason && (
                <Text variant="text-body-meta-regular" color="text-error">
                  {errors.reason.message}
                </Text>
              )}

              {selectedReason && (
                <Controller
                  name="reasonDescription"
                  control={control}
                  rules={{
                    required: isOtherSelected ? 'Please share your reason for cancelling' : false,
                  }}
                  render={({ field }) => (
                    <Flex flexDirection="column" gap="spacer-2">
                      <Text variant="text-body-meta-regular" color="text-primary">
                        {isOtherSelected
                          ? 'Please share your reason for cancelling'
                          : "Anything else you'd like to share?"}
                      </Text>
                      <Textarea {...field} placeholder="Type your response here..." rows={4} />
                      {errors.reasonDescription && (
                        <Text variant="text-body-meta-regular" color="text-error">
                          {errors.reasonDescription.message}
                        </Text>
                      )}
                    </Flex>
                  )}
                />
              )}
            </Flex>
          </form>
        </Modal.Body>
        <Modal.Footer bg="background-page" flexDirection="column" gap="spacer-4" p={0}>
          <Flex flexDirection={{ base: 'column-reverse', lg: 'row' }} gap="spacer-4" width="100%">
            <Button variant="primary-alt" size="m" width={{ base: '100%', lg: '50%' }} onClick={onCloseClick}>
              Keep {isTrialUser ? 'trial' : 'membership'}
            </Button>
            <Button
              variant="destroy"
              size="m"
              width={{ base: '100%', lg: '50%' }}
              onClick={handleSubmit(onFormSubmit)}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Cancel {isTrialUser ? 'trial' : 'membership'}
            </Button>
          </Flex>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
